import { showError } from '@calendly/alert';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { CloseCircleIcon, DeleteIcon, FlagIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { BareItem, Item } from '@calendly/ui/components/menu';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useContext, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { useDestroyTrackedMeetingMutation } from 'hooks/tracked_meetings/api';
import { isCalendlyMeeting } from 'shared/utils';
import { MeetingDetailDependenciesContext } from '../meeting_detail_context';
import { BookFollowUpMeetingButton } from './book_follow_up_meeting_button';
import { isEventInProgress } from '../../../../shared/utils/get_badge_key';
const itemContentsCss = "i1r8mrcv";
const ItemContents = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(itemContentsCss, props.className)} />;
});
const styledMenuIsPhoneCss = "swj2awv";
const styledMenuCss = "s42bdot";
const StyledMenu = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <ViewportDropdownMenu ref={ref} {...props} className={classNames(styledMenuCss, {
    [styledMenuIsPhoneCss]: isPhone
  }, props.className)} />;
});
const RemoveNotetakerItem = ({
  event,
  meetingIntelligence,
  isExpanded,
  toggleDropdown
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('RemoveNotetakerItem must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    transformLocationKind
  } = dependencies;
  const [removeTrackedMeeting, removeTrackedMeetingState] = useDestroyTrackedMeetingMutation();
  const track = useTrack();
  const dispatch = useDispatch();
  const onClickRemove = () => {
    if (removeTrackedMeetingState.isLoading) return;
    if (isEventInProgress(event)) {
      const locationKind = transformLocationKind(event.locationKind);
      dispatch(showError(I18n.t('publisher.meeting_intelligence.notetaker_toggle.cant_remove_notetaker', {
        locationKind
      })));
      return;
    }
    removeTrackedMeeting({
      meetingUuid: meetingIntelligence.trackedMeetingUuid,
      foreignEventId: event.uuid
    });
    track({
      name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
      properties: {
        action: 'disable_notetaker',
        location: 'meetings_page_right_rail',
        is_calendly_meeting: isCalendlyMeeting(event),
        event_uuid: event.uuid,
        mi_event_uuid: meetingIntelligence.trackedMeetingUuid
      }
    });
  };
  useOnValueChange(removeTrackedMeetingState?.isSuccess, () => {
    if (isExpanded && removeTrackedMeetingState?.isSuccess) toggleDropdown();
  });
  return <BareItem onClick={onClickRemove}>
      {removeTrackedMeetingState.isLoading && <Loader size="small" inline />}
      {!removeTrackedMeetingState.isLoading && <ItemContents>
          <CloseCircleIcon />
          {I18n.t('publisher.meeting_intelligence.scheduled_event.remove')}
        </ItemContents>}
    </BareItem>;
};
export const MoreActionsMenu = ({
  event,
  isExpanded,
  meetingIntelligence,
  openCancelModal,
  openDeleteRecapModal,
  shouldShowBookFollowUpMeetingItem,
  shouldShowCancelItem,
  shouldShowDeleteItem,
  shouldShowMenu,
  shouldShowRemoveNotetakerItem,
  shouldShowReportItem,
  showReportModal,
  toggleDropdown
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('MoreActionsMenu must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    getReportEventAnalyticsPayload
  } = dependencies;
  const track = useTrack();
  const sendAnalytics = () => {
    const payload = getReportEventAnalyticsPayload(event);
    track(payload);
  };
  const handleClickReport = () => {
    sendAnalytics();
    showReportModal();
  };
  if (!shouldShowMenu) return null;
  return <StyledMenu>
      {shouldShowRemoveNotetakerItem && <RemoveNotetakerItem event={event} meetingIntelligence={meetingIntelligence} isExpanded={isExpanded} toggleDropdown={toggleDropdown} />}
      {shouldShowBookFollowUpMeetingItem && <BookFollowUpMeetingButton event={event} inDropdown />}
      {shouldShowCancelItem && <Item onClick={openCancelModal}>
          <ItemContents>
            <DeleteIcon />
            {I18n.t('publisher.action.cancel')}
          </ItemContents>
        </Item>}
      {shouldShowDeleteItem && <Item onClick={openDeleteRecapModal}>
          <ItemContents>
            <DeleteIcon />
            {I18n.t('publisher.meeting_intelligence.details.header.delete')}
          </ItemContents>
        </Item>}
      {shouldShowReportItem && <Item onClick={handleClickReport}>
          <ItemContents>
            <FlagIcon />
            {I18n.t('publisher.meetings.report_event')}
          </ItemContents>
        </Item>}
    </StyledMenu>;
};

require("./more_actions_menu.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./more_actions_menu.js");